import { useState } from 'react';

import clsx from 'clsx';

import { useCheckoutLink } from '@/utils/hooks/swr/pricing/useCheckoutLink';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
// Utils
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Types
import type { ActivePlanType } from '@/types/pricingTypes/subscriptionType';

import Loader from '@/components/base/Loader';
// Components
import Modal from '@/components/base/Modal';
import { IframeCheckoutChangePayment } from '@/components/pages/settings/subscription-invoices/UserBilling/ChangePaymentModal/IframeCheckoutChangePayment';

type ChangePaymentModalProps = {
    isOpen: boolean;
    closeModal: () => void;
};

export function ChangePaymentModal({ isOpen, closeModal }: ChangePaymentModalProps) {
    const { subscription } = useSubscription();

    if (!subscription?.product_key || subscription?.product_key === 'trial') {
        return null;
    }

    return (
        <Modal open={isOpen} onDismiss={closeModal} className="z-50">
            <ChangePaymentModalContent
                productId={subscription.product_id}
                closeModal={closeModal}
            />
        </Modal>
    );
}

type ChangePaymentModalContentProps = Pick<ChangePaymentModalProps, 'closeModal'> & {
    productId: ActivePlanType['product_id'];
};

function ChangePaymentModalContent({ productId }: ChangePaymentModalContentProps) {
    const { t } = useTranslation('settings');
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);
    const { mutateUser } = useUser();
    const { isLoading: isLoadingSubscription, mutateSubscription } = useSubscription();

    function handleSuccess() {
        mutateUser();
        mutateSubscription();
    }

    const { checkoutLink, isLoading: isLoadingCheckoutLink } = useCheckoutLink({
        product_id: productId
    });

    return (
        <Modal.Body>
            <Modal.Title>{t('subscription_invoices_page.change_payment_method')}</Modal.Title>
            <Modal.Content className="relative pb-12 pt-m text-center font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                {!isLoadingCheckoutLink && !isLoadingSubscription ? (
                    <div
                        className={clsx('min-h-[380px]', {
                            'opacity-0': !isIframeLoaded
                        })}>
                        <IframeCheckoutChangePayment
                            customerId={checkoutLink.customer_id}
                            onSuccess={handleSuccess}
                            isIframeLoaded={isIframeLoaded}
                            setIsIframeLoaded={setIsIframeLoaded}
                        />
                    </div>
                ) : null}
                {!isIframeLoaded ? (
                    <div className="absolute left-0 top-0 z-0 flex h-[380px] w-full items-center justify-center">
                        <Loader className="h-8 w-8 text-icoBlue" />
                    </div>
                ) : null}
            </Modal.Content>
        </Modal.Body>
    );
}
